<template>
  <header :class="cn(['', `--${isHome && isOnLanding ? 'sticky' : 'default'}`])">
    <HeaderBanner />

    <div :class="cn('inner')">
      <ShopifyLinks
        :class="cn(['menu', 'menu--main'])"
        aria-label="Main navigation"
        :links="main.links"
      />

      <div :class="cn('logo')" :style="isHome ? { transform: `translateY(${logoTranslate}px)` } : {}">
        <a aria-label="Home" href="/"><SvgLogo /></a>
      </div>

      <ShopifyLinks
        :class="cn(['menu', 'menu--account'])"
        aria-label="Account navigation"
        :links="account.links"
      />
    </div>
  </header>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { LiquidLinkList } from '../../types/liquid'
import SvgLogo from '../svg/SvgLogo.vue'
import HeaderBanner from './HeaderBanner.vue'
import ShopifyLinks from '../shopify/link/ShopifyLinks.vue'
import { bem } from '../../utils/classnames'

const START_TOP = 100
const FIXED_TOP = 20

function useLogoTranslate() {
  const isOnLanding = ref(true)
  // The transform offset equals (current top position - FIXED_TOP)
  // At load: (200 - 20) = 180, and once scrolled far enough it reaches 0.
  const logoTranslate = ref(START_TOP - FIXED_TOP)

  const onScroll = () => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
    const currentTop = Math.max(START_TOP - scrollTop, FIXED_TOP)
    logoTranslate.value = currentTop - FIXED_TOP
    isOnLanding.value = currentTop > FIXED_TOP
  }

  onMounted(() => window.addEventListener('scroll', onScroll))
  onUnmounted(() => window.removeEventListener('scroll', onScroll))

  return { isOnLanding, logoTranslate }
}

const { isOnLanding, logoTranslate } = useLogoTranslate()
const isHome = window.location.pathname === '/'
const cn = bem('header-desktop')

const props = defineProps<{
  main: LiquidLinkList
  account: LiquidLinkList
}>()
</script>

<style lang="scss">
@import '~styles/base.scss';

.shopify-link-search__title.shopify-link__text {
  font-size: var(--font-xs);
  line-height: var(--font-lh-xs);
  letter-spacing: 0.02em !important;
}

.header-desktop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  pointer-events: none;

  @include breakpoint('l') {
    display: none;
  }

  &__inner {
    padding: 16px var(--inset);
    display: grid;
    grid-template-columns: 1fr 150px 1fr;
    gap: 10px;
    line-height: 1.4em;
  }

  &__logo {
    display: block;
    pointer-events: auto;
    height: fit-content;
  }

  &__menu {
    &--main {
      > * {
        pointer-events: auto;
        margin-right: auto;
      }
    }

    &--account {
      pointer-events: auto;
      margin-left: auto;
      text-align: right;
      text-transform: uppercase;
      font-size: var(--font-xs);
      line-height: 1.4em;
      letter-spacing: 0.03em;
    }
  }

  .shopify-link-dropdown {
    &__title {
      @include type-xs-uppercase;
      margin-top: 0px;
      margin-bottom: 0px;
      letter-spacing: 0.04em;
    }

    &:first-child &__title {
      margin-top: 0;
    }

    &__links {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      padding-top: 0px;
      min-width: 240px;

      .shopify-link {
        padding-left: 12px;
        color: var(--color-midgrey);
        position: relative;
        line-height: 12px;

        &__text {
          text-transform: none;
        }

        &--space {
          visibility: hidden;
          height: 8px;
        }

        &--submenu {
          margin-top: 16px;
          margin-bottom: 8px;
          color: var(--color-dark);
          padding-left: 0 !important;
          transition: none !important;

          &:after {
            display: none;
          }
        }

        &--default + &--submenu {
          margin-top: 16px;
        }

        &--heading:not(:first-of-type) {
          margin-top: 8px;
        }

        &--heading {
          @include type-xxs-uppercase;
          color: var(--color-dark);
          margin-top: 0px;
          margin-bottom: 0px;
          letter-spacing: 0.05em;

          @include hover {
            &:after {
              display: none;
            }
          }
        }

        &:first-child:not(.shopify-link--heading):not(.shopify-link--submenu) {
          margin-top: 12px;
        }

        &:last-child {
          margin-bottom: 16px;
        }

        &:after {
          position: absolute;
          width: 3px;
          height: 3px;
          background: var(--color-dark);
          border-radius: 3px;
          content: '';
          left: 4px;
          top: calc(50% - 3px);
          opacity: 0;
          transform: translateY(5px);
          transition: opacity 0.2s ease-out, transform 0.1s ease-out;
        }

        &.shopify-link--active {
          color: var(--color-dark);

          &:after {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @include hover {
          color: var(--color-dark);

          &:after {
            opacity: 1;
            transform: translateY(0);
          }
        }
      }

      .shopify-link a[href*='archive'] {
        margin-bottom: 6px;
      }
    }
  }

  .shopify-links {
    display: flex;
    flex-direction: column;
  }

  span.shopify-link.shopify-link--login.shopify-link--depth-0.shopify-link--index-1 {
    margin-top: 2px;
  }

  button.shopify-link.shopify-link--cart.shopify-link--depth-0.shopify-link--index-0 {
    margin-top: 2px;
  }
}
</style>
